import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types';
import QuestionInfo from './QuestionInfo';
import UploadPhotoModal from './UploadPhotoModal';
import { nextStepIsStop, photosAvailable, photosRequired, photosOptional } from './Question';
import { useImageStore } from '../../lib/stores/image'
import loadImage from 'blueimp-load-image';
import __ from '../../helpers/translation';
import {compressImage} from "../../helpers/OfflineCommonHandlers";

/**
 * PropTypes for the QuestionYesNo component.
 * 
 * @property {Object} current - The current state object, required.
 * @property {Function} saveProgress - Function to save progress, required.
 * @property {Function} [onAnswer] - Optional function to handle answer events.
 */
const propTypes = {
  current: PropTypes.object.isRequired,
  saveProgress: PropTypes.func.isRequired,
  onAnswer: PropTypes.func
};

/**
 * QuestionYesNo component renders a yes/no question with optional photo upload functionality.
 * 
 * @component
 * @param {Object} props - The properties object.
 * @param {Object} props.current - The current question object.
 * @param {Object} props.current.answer - The current answer.
 * @param {Array} props.current.images - The list of images associated with the current question.
 * @param {Function} props.onAnswer - Callback function to handle the answer submission.
 * @param {Function} props.saveProgress - Callback function to save the progress.
 * 
 * @returns {React.Fragment} The rendered component.
 */
const QuestionYesNo = (props) => {

  const [ errors, setErrors ] = useState('');

  //#region Old code (needs refactoring)

    const toNextQuestion = () => {
        window.$('#uploadPhotoModal').modal('hide');
        props.onAnswer(props.current.answer);
    }

    const answerWithValue = (answer) => {

      if(withPhotos()) {
        props.saveProgress(answer);

        if(nextStepIsStop(props.current, answer)){
            window.$('#stopReasonModal').modal('show');
            return;
        }

        if(!uploadedImages.length && photosAvailable(props.current)){
            window.$('#uploadPhotoModal').modal('show');
            return;
        }
      }

      props.onAnswer(answer);
    };

    const withPhotos = () => {
      return props.current && photosAvailable(props.current);
    }

  //#endregion
  
  const { 
    images, 
    removeImage,
  } = useImageStore();

  /**
   * Only retrieve the images that are associated with the current question. These can be displayed in the modal.
   * 
   * @type {Array<Image>}
   * @see `imageSchema` for the image object structure.
   */
  const uploadedImages = useMemo(() => {
    return Array.from(images.values()).filter(image => image.questionId === props.current.question_id);
  }, [images])

  return (
    <>
      {/* Yes button */}
      <button 
        className={`btn btn-light bs-toggle ${props.current.answer == '1' ? 'toggled' : ''}`}
        onClick={() => answerWithValue('1')}
      >
        {__('app.questions.yes_no_answer_yes')}
      </button>

      {/* No button */}
      <button
        className={`btn btn-light bs-toggle ${props.current.answer == '0' ? 'toggled' : ''}`}
        onClick={() => answerWithValue('0')}
      >
        {__('app.questions.yes_no_answer_no')}
      </button>

      {/* Render photo specific info, if included */}
      {withPhotos() && (
        <>

          {/* Preview images */}
          <div className='row preview-images images !tw-mt-8'>
            {uploadedImages.map(image => (
              <div key={image.hash} className='col-12 col-md-6 col-lg-4'>
                <div className="img" style={{ backgroundImage: `url(${image.dataUri})` }}>
                  <i className="far fa-times delete-image" onClick={() => removeImage(image.hash)}></i>
                </div>
              </div>
            ))}
          </div>

          {/* Upload photo modal */}
          <UploadPhotoModal  
            errors={errors}
            continue={toNextQuestion}
            {...props}
          />

        </>
      )}

      {/* Question info */}
      <QuestionInfo {... props} />

    </>
  );
  
}

QuestionYesNo.propTypes = propTypes;

export default QuestionYesNo;